import React from "react"
import * as Yup from "yup"
import MySimpleSpinner from "@common-utils/MySimpleSpinner"
import { CONST_HTTP_CUSTOM_CODE_FORM_VALIDATION_ERROR } from "@common-constants/*"
import { CONST_API_UTILS_GET_ENTITES_ENDPOINT } from "../../../../endpoints"
import { Form, Formik, FormikHelpers } from "formik"
import MySelectGetAllDataFromServer from "@common-utils/fields/MySelectGetAllDataFromServer"
import { toast } from "react-toastify"
import { AxiosError } from "axios"
import { IHttpErrorResponseModel } from "@common-models/*"
import MyInputField from "@common-utils/fields/MyInputField"
import { useAuth } from "../../../../AppContext"
import {
     ICandidatDocoGenererEtNotifierCandidatParMailFormPropsModel,
     ICandidatGenererDocoEtNotifierCandidatParMailFormikModel,
     ICandidatGenererDocoEtNotifierCandidatParMailRequestModel,
} from "./core/_models"
import { genererDocoEtNotifierCandidatParMail } from "../core/_requests"

const CandidatGenererDocoEtNotifierCandidatParMailForm = ({ candidat, handleSubmitCallBack }: ICandidatDocoGenererEtNotifierCandidatParMailFormPropsModel) => {
     const { currentUser } = useAuth()

     const schema = Yup.object().shape({
          titreDocument: Yup.string().label(`"Titre du document"`).required(),
          mailDestinataire: Yup.string().email().label(`"Mail destinataire"`).required(),
          entiteDoco: Yup.number().label(`"Entité"`).required(),
     })

     const initialValues: ICandidatGenererDocoEtNotifierCandidatParMailFormikModel = {
          entiteDoco: currentUser?.mainEntite?.id as number,
          mailDestinataire: candidat.email,
          titreDocument: "",
     }

     function handleSubmit(values: ICandidatGenererDocoEtNotifierCandidatParMailFormikModel, helpers: FormikHelpers<ICandidatGenererDocoEtNotifierCandidatParMailFormikModel>) {
          helpers.setStatus(null)
          const payload: ICandidatGenererDocoEtNotifierCandidatParMailRequestModel = {
               titreDocument: values.titreDocument as string,
               mailDestinataire: values.mailDestinataire as string,
               entiteDoco: values.entiteDoco as number,
          }
          genererDocoEtNotifierCandidatParMail(candidat.id, payload)
               .then(r => {
                    toast.success("Le DoCo a bien été généré. Le candidat recevra un courriel contenant les instructions.")
                    handleSubmitCallBack(r.data)
               })
               .catch((e: AxiosError) => {
                    const error: IHttpErrorResponseModel = e.response?.data
                    if (error?.code === CONST_HTTP_CUSTOM_CODE_FORM_VALIDATION_ERROR && error?.errors) {
                         for (const key in error.errors) helpers.setFieldError(key, error.errors[key])
                    }
                    helpers.setStatus(error?.detail)
                    toast.error(error?.detail, { autoClose: false })
                    helpers.setSubmitting(false)
               })
     }

     return (
          <div className="p-2">
               <div className="d-flex align-items-center mb-6 bg-light-primary rounded p-4 border border-primary border-dashed">
                    <span className="svg-icon svg-icon-2hx svg-icon-primary me-4">
                         <i className="fas fa-robot fs-1 text-primary"></i>
                    </span>
                    <div className="text-gray-700 fw-bold fs-6">Cette action va générer un DoCo vierge dans l'espace du candidat et lui envoyer une notification par email.</div>
               </div>

               <Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={schema}>
                    {helpers => (
                         <Form noValidate autoComplete="off">
                              <div className="row g-5">
                                   <div className="col-12">
                                        <label className="form-label fw-bolder text-dark fs-6 required">Titre du DoCo</label>
                                        <MyInputField
                                             name={"titreDocument"}
                                             value={helpers.values.titreDocument}
                                             isInvalid={!!helpers.errors.titreDocument}
                                             placeholder={"Ex: Dossier de Compétences 2024"}
                                             onChange={value => helpers.setFieldValue("titreDocument", value)}
                                        />
                                        <div className="text-muted fs-8 mt-1">Ce titre sera visible par le candidat.</div>
                                        {helpers.errors.titreDocument && <div className="text-danger mt-1">{helpers.errors.titreDocument}</div>}
                                   </div>

                                   <div className="col-md-6">
                                        <label className="form-label fw-bolder text-dark fs-6 required">Email destinataire</label>
                                        <MyInputField
                                             name={"mailDestinataire"}
                                             placeholder={"email@exemple.com"}
                                             value={helpers.values.mailDestinataire}
                                             isInvalid={!!helpers.errors.mailDestinataire}
                                             onChange={value => helpers.setFieldValue("mailDestinataire", value)}
                                        />
                                        {helpers.errors.mailDestinataire && <div className="text-danger mt-1">{helpers.errors.mailDestinataire}</div>}
                                   </div>

                                   <div className="col-md-6">
                                        <label className="form-label fw-bolder text-dark fs-6 required">Entité Émettrice</label>
                                        <MySelectGetAllDataFromServer
                                             name={"entiteDoco"}
                                             value={helpers.values.entiteDoco}
                                             defaultOptions={[{ value: currentUser?.mainEntite?.id as number, label: currentUser?.mainEntite?.nom as string }]}
                                             onChange={val => helpers.setFieldValue("entiteDoco", val)}
                                             url={CONST_API_UTILS_GET_ENTITES_ENDPOINT}
                                             isInvalid={!!helpers.errors.entiteDoco}
                                        />
                                        {helpers.errors.entiteDoco && <div className="text-danger mt-1">{helpers.errors.entiteDoco}</div>}
                                   </div>
                              </div>

                              <div className="separator separator-dashed my-6"></div>

                              <div className="d-flex justify-content-end">
                                   <button className="btn btn-primary fw-bold px-6" type="submit" disabled={helpers.isSubmitting}>
                                        {!helpers.isSubmitting ? (
                                             <>
                                                  <i className="fas fa-paper-plane me-2"></i> Générer et Envoyer
                                             </>
                                        ) : (
                                             <MySimpleSpinner size={"sm"} />
                                        )}
                                   </button>
                              </div>
                         </Form>
                    )}
               </Formik>
          </div>
     )
}

export default CandidatGenererDocoEtNotifierCandidatParMailForm
